import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/components/AuthContext';
import { BookOpen, CheckCircle, Loader2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('polling');
  const [attempts, setAttempts] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }

    const poll = async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/payments/status/${sessionId}`
        );
        if (data.payment_status === 'paid') {
          setStatus('paid');
          await refreshUser();
          setTimeout(() => navigate('/dashboard'), 2500);
          return;
        }
        if (data.payment_status === 'failed' || data.payment_status === 'expired') {
          setStatus('failed');
          return;
        }
        setAttempts(a => a + 1);
      } catch (err) {
        setStatus('error');
      }
    };

    poll();
    timerRef.current = setInterval(poll, 2000);
    return () => clearInterval(timerRef.current);
  }, [sessionId, navigate, refreshUser]);

  useEffect(() => {
    if (attempts > 30) {
      clearInterval(timerRef.current);
      setStatus('timeout');
    }
  }, [attempts]);

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-6 paper-texture">
      <div className="w-full max-w-md text-center">
        <div className="inline-flex items-center gap-2 mb-8">
          <BookOpen className="w-8 h-8 text-[#6B7A4F]" />
          <span className="text-2xl font-bold text-[#2D2A26]">Bookish Bestie</span>
        </div>
        <div className="glass-card p-10 rounded-2xl" data-testid="payment-success-container">
          {status === 'polling' && (
            <>
              <Loader2 className="w-16 h-16 text-[#6B7A4F] animate-spin mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-[#2D2A26] mb-2">Confirming your payment...</h2>
              <p className="text-[#5C5955]">This will only take a moment.</p>
            </>
          )}
          {status === 'paid' && (
            <>
              <CheckCircle className="w-16 h-16 text-[#8E9E82] mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-[#2D2A26] mb-2">Payment Successful!</h2>
              <p className="text-[#5C5955] mb-2">Welcome to Bookish Bestie 🎉</p>
              <p className="text-sm text-[#5C5955]">Redirecting to your dashboard...</p>
            </>
          )}
          {status === 'failed' && (
            <>
              <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-[#2D2A26] mb-2">Payment Failed</h2>
              <p className="text-[#5C5955] mb-4">Something went wrong. Please try again.</p>
              <Link to="/checkout"><Button className="bg-[#6B7A4F] hover:bg-[#57653F] text-white rounded-full">Try Again</Button></Link>
            </>
          )}
          {(status === 'timeout' || status === 'error') && (
            <>
              <XCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-[#2D2A26] mb-2">Taking longer than expected</h2>
              <p className="text-[#5C5955] mb-4">Your payment might still be processing. Check your dashboard in a moment.</p>
              <Link to="/dashboard"><Button className="bg-[#6B7A4F] hover:bg-[#57653F] text-white rounded-full">Go to Dashboard</Button></Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
